import * as THREE from 'three';
import { GRASS, BUILD, TERRAIN } from '../config';
import { terrainHeight } from './terrain';
import { grassDensityAt, grassBladeScale, grassBladeTall, grassTintAt, groundTint } from './biome';
import { cloudFieldGLSL, cloudUniforms } from './clouds';
import { TRAIL_GLSL, bindTrailUniforms } from './trail';
import { WIND_DIR, WIND_GUST } from './wind';
import { INTERIOR_FRAG_DECL, interiorDarkGLSL, bindInteriorUniforms } from './interior';

// Dense stylized grass after James-Smyth/three-grass-demo, adapted to an infinite follow-world.
// Each chunk is ONE baked mesh of 5-vertex blades (not instanced); the blade color comes from a
// procedural grass texture sampled by world position, multiplied by a drifting cloud texture for
// moving shadows; the tip sways most via a per-vertex wind weight. Short (foot height), and a
// CARPET (tens of blades per square unit). A grid of chunks recenters as the player moves; off-edge
// chunks are rebuilt deterministically per world cell. Cosmetic, client-only.

const FOUND_S = BUILD.size;
const FOUND_TRI_R = BUILD.size / Math.sqrt(3);
const CELL = GRASS.cell;
const BLADES = Math.round(GRASS.density * CELL * CELL); // blades per chunk
const VPER = 5; // verts per blade
// Coarse heightmap resolution per chunk. Drives the per-blade base height (bilinear-sampled), slope
// and biome. At ~1m spacing (CELL/HM_R) the bilinear height is sub-cm off the analytic ground -- far
// finer than the rendered terrain (3.6m quads) -- so it replaces the per-blade terrainHeight() call
// (~30x fewer noise evaluations per chunk, which is what made the recenter rebuild hitch).
const HM_R = 12;

type Foundation = { type: 'square' | 'triangle'; x: number; z: number; yaw: number };

interface Chunk {
  mesh: THREE.Mesh;
  geom: THREE.BufferGeometry;
  pos: Float32Array;
  uv: Float32Array;
  gy: Float32Array; // base terrain Y per vertex (lets the shader grow blades in from the ground)
  tint: Float32Array; // per-vertex grass-blade tint (linear RGB); (0,0,0) = default green gradient
  root: Float32Array; // per-vertex TERRAIN ground colour at the blade base -- the root blends into it
  cellX: number;
  cellZ: number;
  ready: boolean;  // baked for its current cell (hidden until true, so a stale cell never shows)
  queued: boolean; // already in the rebake queue (avoids double-enqueue)
}

function smoothstep01(a: number, b: number, x: number): number {
  const t = x < a ? 0 : x > b ? 1 : (x - a) / (b - a);
  return t * t * (3 - 2 * t);
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function cellSeed(cx: number, cz: number): number {
  let h = (Math.imul(cx, 374761393) + Math.imul(cz, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

// --- tileable value noise for the procedural textures ---
function rng2(ix: number, iz: number, seed: number): number {
  let h = (Math.imul(ix, 374761393) + Math.imul(iz, 668265263) + Math.imul(seed, 69069)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function vnoiseT(u: number, v: number, period: number, seed: number): number {
  const x = u * period, y = v * period;
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const x0 = ((xi % period) + period) % period, x1 = (x0 + 1) % period;
  const y0 = ((yi % period) + period) % period, y1 = (y0 + 1) % period;
  const a = rng2(x0, y0, seed), b = rng2(x1, y0, seed), c = rng2(x0, y1, seed), d = rng2(x1, y1, seed);
  return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
}
function fbmT(u: number, v: number, period: number, oct: number, seed: number): number {
  let amp = 1, sum = 0, norm = 0, p = period;
  for (let i = 0; i < oct; i++) {
    sum += amp * vnoiseT(u, v, p, seed + i * 71);
    norm += amp;
    amp *= 0.5;
    p *= 2;
  }
  return sum / norm;
}
function clamp01(x: number): number { return x < 0 ? 0 : x > 1 ? 1 : x; }

// A lush, varied green grass color texture (sampled by world position -> the field's color map).
function makeGrassTexture(): THREE.CanvasTexture {
  const size = 512;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size, v = y / size;
      const base = fbmT(u, v, 6, 4, 11);
      const patch = fbmT(u, v, 3, 3, 23);
      const fine = vnoiseT(u, v, 32, 31) - 0.5;
      const lush = clamp01(base * 0.55 + patch * 0.55);
      let r = 0.10 + 0.26 * lush + 0.07 * fine;
      let g = 0.27 + 0.42 * lush + 0.06 * fine;
      let b = 0.07 + 0.12 * lush;
      const dry = Math.max(0, patch - 0.62) * 2.0; // dry yellow-tan patches
      r += dry * 0.24; g += dry * 0.16; b -= dry * 0.05;
      const i = (y * size + x) * 4;
      img.data[i] = clamp01(r) * 255;
      img.data[i + 1] = clamp01(g) * 255;
      img.data[i + 2] = clamp01(b) * 255;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 8; // grazing-angle sampling: the single biggest win against zoom-out shimmer/repeat read
  return t;
}

// Soft drifting cloud-shadow texture: mostly light (1.0 = full sun) with soft darker patches.
function makeCloudTexture(): THREE.CanvasTexture {
  const size = 256;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const c = fbmT(x / size, y / size, 4, 4, 7);
      const g = 0.5 + 0.5 * (1 - clamp01((c - 0.32) / 0.4)); // light, soft dark blobs
      const i = (y * size + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = clamp01(g) * 255;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.NoColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 8;
  return t;
}

// Build the LUSH grass material (dedekpo/stylized-scene technique, reimplemented in our GLSL/WebGL path on
// the same blades): a circular-arc cantilever bend driven by a slow gust + faster chop + a tip flutter; a
// two-tone root->tip colour gradient (pow 1.4) with organic patch variation; and a sun BACK-LIGHT so the
// translucent tips glow when viewed toward the sun. Shares the live uTime/uFocus/uDay/uTorch uniforms, so it
// dims with the day-night cycle and lights up under a torch exactly like the classic look.
function makeLushGrassMaterial(
  grassTex: THREE.Texture,
  cloudTex: THREE.Texture,
  u: { uTime: { value: number }; uFocus: { value: THREE.Vector2 }; uDay: { value: number }; uTorch: { value: THREE.Vector4 }; uSunDir: { value: THREE.Vector3 }; uPlayer: { value: THREE.Vector3 } },
): THREE.MeshBasicMaterial {
  const g = GRASS;
  const material = new THREE.MeshBasicMaterial({ map: grassTex, side: THREE.DoubleSide });
  material.onBeforeCompile = (shader) => {
    shader.uniforms.iTime = u.uTime;
    shader.uniforms.cloudTex = { value: cloudTex };
    shader.uniforms.uFocus = u.uFocus;
    shader.uniforms.uDay = u.uDay;
    shader.uniforms.uTorch = u.uTorch;
    shader.uniforms.uSunDir = u.uSunDir;
    shader.uniforms.uPlayer = u.uPlayer;
    shader.uniforms.uCloudCover = cloudUniforms.uCloudCover;
    shader.uniforms.uCullR = { value: g.cullRadius };
    shader.uniforms.uFade = { value: g.fadeWidth };
    shader.uniforms.uWindFull = { value: g.windFull };
    shader.uniforms.uWindCalm = { value: g.windCalm };
    shader.uniforms.uWindDir = WIND_DIR;   // global wind direction (grass leans along it)
    shader.uniforms.uWindGust = WIND_GUST; // global gust strength (scales the lean, kept limited for grass)
    shader.uniforms.uLodStart = { value: g.lodStart };
    bindTrailUniforms(shader);
    bindInteriorUniforms(shader); // sealed-base interior dim reaches the grass too (it used to be hidden)
    shader.vertexShader =
      'attribute float windWeight;\nattribute float groundY;\nattribute vec3 tint;\nattribute vec3 rootCol;\nuniform float iTime;\nuniform vec2 uFocus;\nuniform vec3 uPlayer;\nuniform float uCullR;\nuniform float uFade;\nuniform float uWindFull;\nuniform float uWindCalm;\nuniform vec2 uWindDir;\nuniform float uWindGust;\nuniform float uLodStart;\nvarying vec2 vWXZ;\nvarying float vBladeH;\nvarying vec3 vViewDir;\nvarying vec3 vTint;\nvarying vec3 vRootCol;\nfloat hashGrass(vec2 p){ p = mod(p, 289.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }\n' + TRAIL_GLSL + '\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vWXZ = position.xz;
        vBladeH = windWeight;
        vTint = tint;
        vRootCol = rootCol;
        float fd = distance(position.xz, uFocus);
        // LOD: out past uLodStart a growing fraction of blades (picked by a stable per-blade hash) collapse to
        // nothing, so distant grass is sparse + cheap + free of sub-pixel shimmer; near grass stays full.
        vec2 lcell = floor(position.xz * 3.3);
        float lodCut = step(hashGrass(lcell), smoothstep(uLodStart, uCullR, fd) * 0.7);
        float t = clamp(windWeight, 0.0, 1.0);
        // Arc-bend wind: a slow travelling gust + a faster chop, then a tip flutter. Bend grows with height.
        float gust = pow(sin(iTime * 0.6 + position.x * 0.05 + position.z * 0.035) * 0.5 + 0.5, 1.6);
        float chop = sin(iTime * 1.7 + position.x * 0.12 + position.z * 0.09) * 0.5 + 0.5;
        float bend = gust * 0.75 + chop * 0.25;
        float windAmp = ${(g.windTip * 2.2).toFixed(3)} * (1.0 - smoothstep(uWindFull, uWindCalm, fd));
        // Lean along the GLOBAL wind direction, amplitude scaled by the live gust but kept LIMITED for grass
        // (never much past 1x) so it ripples with the same gust as the canopies without thrashing.
        float gustAmp = 0.45 + uWindGust * 0.5;
        float sway = bend * windAmp * pow(t, 1.3) * gustAmp;
        float flutter = sin(iTime * 9.0 + position.x * 0.6) * 0.05 * smoothstep(0.5, 1.0, t);
        transformed.x += sway * uWindDir.x + flutter;
        transformed.z += sway * uWindDir.y;
        transformed.y -= bend * windAmp * pow(t, 2.0) * 0.35 * gustAmp;  // tip drops as the blade leans over
        // PLAYER TREAD: blades within ~1m of the feet bow away and duck, so walking parts the grass.
        // Height-gated (uPlayer.y near the blade's ground) so a player on a cliff above does not part it.
        vec2 toP = transformed.xz - uPlayer.xz;
        float pd = length(toP);
        float tread = (1.0 - smoothstep(0.12, 1.05, pd)) * (1.0 - smoothstep(1.6, 2.6, abs(uPlayer.y - groundY)));
        vec2 pushDir = pd > 1e-4 ? toP / pd : vec2(1.0, 0.0);
        transformed.x += pushDir.x * tread * 0.45 * t;
        transformed.z += pushDir.y * tread * 0.45 * t;
        transformed.y -= tread * 0.30 * t;
        // TRODDEN TRAIL: where the player has walked (last ~60s) blades LIE FLAT and slowly rise back as
        // the map decays. Each blade leans a stable pseudo-random way so the trail reads matted, not combed.
        float trod = oxTrail(position.xz);
        if (trod > 0.004) {
          vec2 tdir = normalize(vec2(hashGrass(position.xz * 7.31) - 0.5, hashGrass(position.xz * 3.17 + 11.0) - 0.5) + vec2(1e-4, 0.0));
          transformed.x += tdir.x * trod * 0.55 * t;
          transformed.z += tdir.y * trod * 0.55 * t;
          // Press the whole blade into the earth, not just the tip: the old *t weight left mid-blade
          // standing, so trails read bent instead of matted. Root stays put (its height is ~0 anyway).
          transformed.y -= (transformed.y - groundY) * trod * 0.95 * clamp(t * 2.2, 0.0, 1.0);
        }
        float gfade = 1.0 - smoothstep(uCullR - uFade, uCullR, fd);
        transformed.y = mix(groundY, transformed.y, gfade);
        if (lodCut > 0.5) transformed = vec3((lcell.x + 0.5) / 3.3, groundY, (lcell.y + 0.5) / 3.3); // collapse to a point
        vViewDir = normalize(cameraPosition - transformed);`,
      );
    shader.fragmentShader =
      'uniform sampler2D cloudTex;\nuniform float iTime;\nuniform float uDay;\nuniform vec4 uTorch;\nuniform vec3 uSunDir;\nuniform float uCloudCover;\nvarying vec2 vWXZ;\nvarying float vBladeH;\nvarying vec3 vViewDir;\nvarying vec3 vTint;\nvarying vec3 vRootCol;\n' + INTERIOR_FRAG_DECL + '\n' + cloudFieldGLSL + '\n' +
      shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        float gradT = pow(clamp(vBladeH, 0.0, 1.0), 1.4);
        vec3 gA = mix(vec3(0.045, 0.13, 0.035), vec3(0.34, 0.58, 0.17), gradT); // cool deep green
        vec3 gB = mix(vec3(0.085, 0.16, 0.05), vec3(0.50, 0.55, 0.16), gradT);  // warmer / yellower
        float clump = 0.5 + 0.5 * sin(vWXZ.x * 0.06 + 1.3) * cos(vWXZ.y * 0.052 - 0.7);
        vec3 col = mix(gA, gB, clump);
        // Per-biome tint: desert/irradiated blades swap the green base for their colour but KEEP the root->tip
        // lightness so they still read as lit blades; green biomes pass tint=(0,0,0) and stay the green gradient.
        // biome.ts blends the tint over a ring, so |vTint| RAMPS 0->full across a desert/irradiated border; fade
        // the AMOUNT in with smoothstep (no hard step = no blade seam), and renormalise the hue to full biome
        // saturation so the blades keep their colour through the fade instead of dimming muddy at the seam.
        float tintMag = length(vTint);
        float tintAmt = smoothstep(0.06, 0.5, tintMag);
        vec3 tintCol = tintMag > 1e-3 ? vTint * (0.68 / tintMag) : vTint;
        col = mix(col, tintCol * (0.4 + 0.9 * gradT), tintAmt);
        // ROOT GROUNDING: the lowest stretch of every blade blends into the TERRAIN'S OWN ground colour
        // at this spot (baked per blade from the same groundTint the terrain splat uses), so roots
        // disappear into whatever they actually grow from -- green field or dirt patch alike.
        col = mix(vRootCol * (0.74 + 0.18 * clump), col, smoothstep(0.0, 0.38, gradT));
        col *= oxCloudShadow(vWXZ, uSunDir, iTime, uCloudCover); // sun-projected, weather-driven cloud shadow (matches terrain + sky)
        float dayLit = clamp(uDay, 0.0, 1.0);
        // Sun back-light: translucent tips glow when the view looks toward the sun (day only).
        float back = pow(max(dot(vViewDir, -normalize(uSunDir)), 0.0), 3.0) * pow(clamp(vBladeH, 0.0, 1.0), 1.5);
        col += vec3(0.55, 0.62, 0.22) * back * dayLit;
        // Day-night dim + warm torch light, same as the classic path so it sits in the lit world.
        float td = distance(vWXZ, uTorch.xy);
        float torchLit = uTorch.z * clamp(1.0 - td / max(uTorch.w, 0.001), 0.0, 1.0);
        torchLit *= torchLit;
        vec3 lit = mix(vec3(0.015, 0.02, 0.035), vec3(1.0), dayLit);
        lit += vec3(1.2, 0.72, 0.4) * torchLit;
        diffuseColor.rgb = col * lit;`,
      ).replace(
        '#include <opaque_fragment>',
        // INTERIOR DIM: inside a sealed base, grass outside dims to silhouettes with the terrain
        // (grass used to be hard-hidden; the owner wants the world visible, just not detailed).
        `#include <opaque_fragment>\n${interiorDarkGLSL('vWXZ')}`,
      );
  };
  material.customProgramCacheKey = () => 'grass-lush11-interior';
  return material;
}

export class GrassField {
  readonly group = new THREE.Group();
  private readonly uTime = { value: 0 };
  private readonly uFocus = { value: new THREE.Vector2() };
  private readonly uDay = { value: 1 }; // 0 night .. 1 full day: the global sun reaches the grass too
  private readonly uTorch = { value: new THREE.Vector4(0, 0, 0, 1) }; // (worldX, worldZ, intensity 0..1, range): a wielded torch lights nearby grass
  private readonly uSunDir = { value: new THREE.Vector3(0, 1, 0) }; // global sun direction -- drives the LUSH grass back-light translucency
  private readonly uPlayer = { value: new THREE.Vector3(0, -999, 0) }; // player feet: nearby blades bow away (tread); parked far below until set
  private classicMat!: THREE.Material; // current flat-blade grass
  private lushMat!: THREE.Material;    // dedekpo-style: arc-bend wind + two-tone gradient + sun translucency
  private style: 'classic' | 'lush' = 'classic';
  private readonly hm = new Float64Array((HM_R + 1) * (HM_R + 1)); // reused coarse heightmap
  private readonly bm = new Float32Array((HM_R + 1) * (HM_R + 1)); // reused coarse grass-density mask (0..1 per biome)
  private readonly sm = new Float32Array((HM_R + 1) * (HM_R + 1)); // reused coarse blade-size mask (1 default, 2 forest)
  private readonly tm = new Float32Array((HM_R + 1) * (HM_R + 1)); // reused coarse blade height-only mask (1 default, forest 2)
  private readonly cm = new Float32Array((HM_R + 1) * (HM_R + 1) * 3); // reused coarse blade tint (linear RGB); (0,0,0)=default green
  private readonly gm = new Float32Array((HM_R + 1) * (HM_R + 1) * 3); // reused coarse TERRAIN ground colour (the root anchors to this)
  private readonly chunks: Chunk[] = [];
  private readonly dirty: Chunk[] = []; // rebake queue, drained a few chunks per frame (no hitch)
  private foundations: Foundation[] = [];
  private burns: Array<{ x: number; z: number; r: number }> = []; // permanent lightning scorch: grass stays cleared here
  private curX = NaN;
  private curZ = NaN;

  constructor() {
    const grassTex = makeGrassTexture();
    const cloudTex = makeCloudTexture();
    const uTime = this.uTime;
    const uFocus = this.uFocus;
    const uDay = this.uDay;
    const uTorch = this.uTorch;
    const g = GRASS;
    const material = new THREE.MeshBasicMaterial({ map: grassTex, side: THREE.DoubleSide });
    material.onBeforeCompile = (shader) => {
      shader.uniforms.iTime = uTime;
      shader.uniforms.cloudTex = { value: cloudTex };
      shader.uniforms.uFocus = uFocus;
      shader.uniforms.uDay = uDay;
      shader.uniforms.uTorch = uTorch;
      shader.uniforms.uPlayer = this.uPlayer;
      shader.uniforms.uCullR = { value: GRASS.cullRadius };
      shader.uniforms.uFade = { value: GRASS.fadeWidth };
      shader.uniforms.uWindFull = { value: GRASS.windFull };
      shader.uniforms.uWindCalm = { value: GRASS.windCalm };
      shader.uniforms.uLodStart = { value: GRASS.lodStart };
      bindTrailUniforms(shader);
      bindInteriorUniforms(shader); // sealed-base interior dim reaches this grass style too
      shader.vertexShader =
        'attribute float windWeight;\nattribute float groundY;\nattribute vec3 rootCol;\nuniform float iTime;\nuniform vec2 uFocus;\nuniform vec3 uPlayer;\nuniform float uCullR;\nuniform float uFade;\nuniform float uWindFull;\nuniform float uWindCalm;\nuniform vec2 uWindDir;\nuniform float uWindGust;\nuniform float uLodStart;\nvarying vec2 vWXZ;\nvarying float vBladeH;\nvarying vec3 vRootCol;\nfloat hashGrass(vec2 p){ p = mod(p, 289.0); vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }\n' + TRAIL_GLSL + '\n' +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          vWXZ = position.xz;
          vRootCol = rootCol;
          vBladeH = windWeight; // 0 base .. 0.5 mid .. 1 tip -- drives the vertical shading gradient
          float fd = distance(position.xz, uFocus);
          // LOD: out past uLodStart a growing fraction of blades collapse to nothing (cheap, no sub-pixel shimmer).
          vec2 lcell = floor(position.xz * 3.3);
          float lodCut = step(hashGrass(lcell), smoothstep(uLodStart, uCullR, fd) * 0.7);
          float amp = windWeight > 0.6 ? ${g.windTip.toFixed(3)} : (windWeight > 0.01 ? ${g.windMid.toFixed(3)} : 0.0);
          amp *= 1.0 - smoothstep(uWindFull, uWindCalm, fd); // near grass sways; distant grass holds still
          float ph = iTime * ${g.windSpeed.toFixed(3)} + position.x * ${g.windWave.toFixed(3)} + position.z * ${(g.windWave * 0.7).toFixed(3)};
          transformed.x += sin(ph) * amp;
          // PLAYER TREAD: blades within ~1m of the feet bow away and duck (height-gated so a cliff above stays put).
          float tw = clamp(windWeight, 0.0, 1.0);
          vec2 toP = transformed.xz - uPlayer.xz;
          float pd = length(toP);
          float tread = (1.0 - smoothstep(0.12, 1.05, pd)) * (1.0 - smoothstep(1.6, 2.6, abs(uPlayer.y - groundY)));
          vec2 pushDir = pd > 1e-4 ? toP / pd : vec2(1.0, 0.0);
          transformed.x += pushDir.x * tread * 0.45 * tw;
          transformed.z += pushDir.y * tread * 0.45 * tw;
          transformed.y -= tread * 0.30 * tw;
          // TRODDEN TRAIL: walked-over blades lie flat for ~60s and rise back as the map decays.
          float trod = oxTrail(position.xz);
          if (trod > 0.004) {
            vec2 tdir = normalize(vec2(hashGrass(position.xz * 7.31) - 0.5, hashGrass(position.xz * 3.17 + 11.0) - 0.5) + vec2(1e-4, 0.0));
            transformed.x += tdir.x * trod * 0.55 * tw;
            transformed.z += tdir.y * trod * 0.55 * tw;
            // Match the near-grass style: press mid-blade down too, so the trail lies truly flat.
            transformed.y -= (transformed.y - groundY) * trod * 0.95 * clamp(tw * 2.2, 0.0, 1.0);
          }
          float gfade = 1.0 - smoothstep(uCullR - uFade, uCullR, fd);
          transformed.y = mix(groundY, transformed.y, gfade);
          if (lodCut > 0.5) transformed = vec3((lcell.x + 0.5) / 3.3, groundY, (lcell.y + 0.5) / 3.3); // collapse to a point`,
        );
      shader.fragmentShader =
        'uniform sampler2D cloudTex;\nuniform float iTime;\nuniform float uDay;\nuniform vec4 uTorch;\nvarying vec2 vWXZ;\nvarying float vBladeH;\nvarying vec3 vRootCol;\n' + INTERIOR_FRAG_DECL + '\n' +
        shader.fragmentShader.replace(
          '#include <map_fragment>',
          `#include <map_fragment>
          diffuseColor.rgb = diffuseColor.rgb * ${g.contrast.toFixed(3)} + ${g.brightness.toFixed(3)};
          vec2 cuv = vWXZ * ${(1 / g.cloudWorldSize).toFixed(5)} + vec2(iTime * ${g.cloudScrollX.toFixed(4)}, iTime * ${g.cloudScrollY.toFixed(4)});
          float shadow = texture2D(cloudTex, cuv).r;
          diffuseColor.rgb *= mix(1.0, shadow, ${g.cloudMix.toFixed(3)});
          // The grass is unlit, so it must dim with the day-night cycle itself or it glows green after dark.
          // Night bottoms out near black (matching the world's near-zero ambient) so it does not pop out of
          // the dark. A wielded torch then lights nearby grass warmly so it reads as part of the lit world.
          float dayLit = clamp(uDay, 0.0, 1.0);
          float td = distance(vWXZ, uTorch.xy);
          float torchLit = uTorch.z * clamp(1.0 - td / max(uTorch.w, 0.001), 0.0, 1.0);
          torchLit *= torchLit; // soft falloff toward the edge of the radius
          vec3 lit = mix(vec3(0.015, 0.02, 0.035), vec3(1.0), dayLit); // near-black night .. full day
          lit += vec3(1.2, 0.72, 0.4) * torchLit;                      // torch adds warm light on top
          diffuseColor.rgb *= lit;
          // Vertical AO/form: blade base in shadow, tip catching light -- gives the grass depth instead of a
          // flat green carpet. Slightly cooler/darker at the root, brighter + warmer at the tip.
          diffuseColor.rgb *= mix(0.55, 1.1, vBladeH);
          diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(1.04, 1.0, 0.92), vBladeH * 0.5);
          // ROOT GROUNDING: the lowest stretch blends into the TERRAIN'S OWN ground colour at this spot
          // (baked per blade from the same groundTint the terrain splat uses), slightly shadowed -- so
          // roots disappear into whatever they actually grow from, green field or dirt patch alike.
          diffuseColor.rgb = mix(vRootCol * 0.82 * lit, diffuseColor.rgb, smoothstep(0.0, 0.4, vBladeH));`,
        ).replace(
          '#include <opaque_fragment>',
          `#include <opaque_fragment>\n${interiorDarkGLSL('vWXZ')}`,
        );
    };
    material.customProgramCacheKey = () => 'grass-smyth9-interior';
    this.classicMat = material;
    // The alternate "lush" look (dedekpo/stylized-scene technique) on the same blades: arc-bend multi-layer
    // wind, a two-tone root->tip gradient with patch noise, and a sun back-light so tips glow toward the sun.
    // Shares the live uniforms so it reacts to day/night + the torch identically. Toggle via setStyle.
    this.lushMat = makeLushGrassMaterial(grassTex, cloudTex, { uTime, uFocus, uDay, uTorch, uSunDir: this.uSunDir, uPlayer: this.uPlayer });

    const N = GRASS.grid * GRASS.grid;
    for (let i = 0; i < N; i++) {
      const pos = new Float32Array(BLADES * VPER * 3);
      const uv = new Float32Array(BLADES * VPER * 2);
      const wind = new Float32Array(BLADES * VPER);
      const gy = new Float32Array(BLADES * VPER);
      const tint = new Float32Array(BLADES * VPER * 3);
      const root = new Float32Array(BLADES * VPER * 3);
      const idx = new Uint32Array(BLADES * 9);
      for (let b = 0; b < BLADES; b++) {
        const o = b * VPER;
        wind[o] = 0; wind[o + 1] = 0; wind[o + 2] = 0.5; wind[o + 3] = 0.5; wind[o + 4] = 1;
        const io = b * 9;
        idx[io] = o; idx[io + 1] = o + 1; idx[io + 2] = o + 2;
        idx[io + 3] = o + 2; idx[io + 4] = o + 4; idx[io + 5] = o + 3;
        idx[io + 6] = o + 3; idx[io + 7] = o; idx[io + 8] = o + 2;
      }
      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      geom.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      geom.setAttribute('windWeight', new THREE.BufferAttribute(wind, 1));
      geom.setAttribute('groundY', new THREE.BufferAttribute(gy, 1));
      geom.setAttribute('tint', new THREE.BufferAttribute(tint, 3));
      geom.setAttribute('rootCol', new THREE.BufferAttribute(root, 3));
      geom.setIndex(new THREE.BufferAttribute(idx, 1));
      const mesh = new THREE.Mesh(geom, material);
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      // Frustum-culled so off-screen chunks skip drawing entirely (the bounding sphere is recomputed
      // after each bake). Distance culling on top keeps only the near disc. Hidden until first baked.
      mesh.frustumCulled = true;
      mesh.visible = false;
      this.group.add(mesh);
      this.chunks.push({ mesh, geom, pos, uv, gy, tint, root, cellX: NaN, cellZ: NaN, ready: false, queued: false });
    }
  }

  // A wielded torch lights nearby grass. Pass its world x/z, intensity (0 = off), and range each frame.
  setTorch(x: number, z: number, intensity: number, range: number): void {
    this.uTorch.value.set(x, z, intensity, range);
  }

  // Global sun direction (normalized, pointing from ground toward the sun). Drives the lush grass back-light.
  setSun(dir: THREE.Vector3): void {
    this.uSunDir.value.copy(dir);
  }

  // The player's feet, each frame: blades within ~1m bow away (the tread effect in both grass styles).
  setPlayer(pos: THREE.Vector3): void {
    this.uPlayer.value.copy(pos);
  }

  // Swap the grass look live (F2 toggle) -- no rebake, just a material swap on every chunk mesh.
  setStyle(style: 'classic' | 'lush'): void {
    if (style === this.style) return;
    this.style = style;
    const mat = style === 'lush' ? this.lushMat : this.classicMat;
    for (const ch of this.chunks) ch.mesh.material = mat;
  }

  get grassStyle(): 'classic' | 'lush' {
    return this.style;
  }

  // Boot loading bar: how many chunks are still queued to bake, and the full count. pumpBake drains a
  // budget on demand so the loader can fill the grass over yielded frames (real progress, no freeze).
  get bakePending(): number { return this.dirty.length; }
  get bakeTotal(): number { return GRASS.grid * GRASS.grid; }
  pumpBake(budget: number): void { this.drain(budget); }

  update(time: number, focus: THREE.Vector3, dayAmount = 1): void {
    this.uTime.value = time;
    this.uFocus.value.set(focus.x, focus.z);
    this.uDay.value = dayAmount;
    const first = !Number.isFinite(this.curX); // first bake: drain fully so spawn has grass at once
    const cx = Math.floor(focus.x / CELL);
    const cz = Math.floor(focus.z / CELL);
    if (cx !== this.curX || cz !== this.curZ) {
      this.curX = cx;
      this.curZ = cz;
      this.recenter();
    }
    // Drain the rebake queue: a bigger budget on the first bake (the boot loading bar covers it + pumpBake
    // finishes it), else a small budget so a boundary crossing never spikes a frame.
    this.drain(first ? GRASS.loadBudget : GRASS.fillBudget);
    const r2 = GRASS.cullRadius * GRASS.cullRadius;
    for (const ch of this.chunks) {
      const dx = (ch.cellX + 0.5) * CELL - focus.x;
      const dz = (ch.cellZ + 0.5) * CELL - focus.z;
      ch.mesh.visible = ch.ready && dx * dx + dz * dz < r2;
    }
  }

  // Re-bake every live chunk in place from the current terrainHeight (kept visible, no flash). Called
  // by the admin panel after a land-gen knob changes, so grass re-seats on the new ground at once.
  rebuildAll(): void {
    for (const ch of this.chunks) {
      if (Number.isFinite(ch.cellX)) this.enqueue(ch, false);
    }
    this.drain(Infinity);
  }

  // Toroidal follow-grid: each cell maps to a fixed slot by wrapping its coordinates, so when the
  // focus crosses a boundary only the NEW edge row's cells change slot (one column/row of `grid`
  // chunks), not the whole grid. Changed chunks are queued for a (deferred) rebake.
  private recenter(): void {
    const G = GRASS.grid;
    const H = (G - 1) / 2;
    for (let ax = this.curX - H; ax <= this.curX + H; ax++) {
      for (let az = this.curZ - H; az <= this.curZ + H; az++) {
        const sx = ((ax % G) + G) % G;
        const sz = ((az % G) + G) % G;
        const ch = this.chunks[sx * G + sz];
        if (ch.cellX !== ax || ch.cellZ !== az) {
          ch.cellX = ax;
          ch.cellZ = az;
          this.enqueue(ch, true); // hide until rebaked: the slot still holds the previous cell's blades
        }
      }
    }
  }

  // Queue a chunk for rebake. `hide` gates its visibility off until the bake completes (used when the
  // cell changed, so a stale cell never flashes); a same-cell re-cut (setFoundations) keeps it visible.
  private enqueue(ch: Chunk, hide: boolean): void {
    if (hide) ch.ready = false;
    if (!ch.queued) {
      ch.queued = true;
      this.dirty.push(ch);
    }
  }

  // Bake up to `budget` queued chunks this frame.
  private drain(budget: number): void {
    let n = 0;
    while (n < budget && this.dirty.length > 0) {
      const ch = this.dirty.shift()!;
      ch.queued = false;
      this.fill(ch);
      ch.ready = true;
      n++;
    }
  }

  // Bake this chunk's blades deterministically for its world cell, applying terrain height, the
  // world-position UV, and the foundation cut (cut blades collapse to a degenerate point).
  private fill(ch: Chunk): void {
    const rng = mulberry32(cellSeed(ch.cellX, ch.cellZ));
    const W = GRASS.bladeWidth, MW = W * 0.5;
    const invTex = 1 / GRASS.texWorldSize;
    const pos = ch.pos, uv = ch.uv, gy = ch.gy;
    // Coarse heightmap over the cell: cheap per-blade base height + slope + biome (no per-blade fbm).
    const N1 = HM_R + 1, x0 = ch.cellX * CELL, z0 = ch.cellZ * CELL, step = CELL / HM_R, hm = this.hm, bm = this.bm, sm = this.sm, tm = this.tm, cm = this.cm, gm = this.gm, tint = ch.tint, root = ch.root;
    for (let j = 0; j < N1; j++) for (let i = 0; i < N1; i++) {
      const wx = x0 + i * step, wz = z0 + j * step, h = terrainHeight(wx, wz);
      hm[j * N1 + i] = h;
      bm[j * N1 + i] = grassDensityAt(wx, wz, h); // 0 on rocky/snow/beach; desert 0.2 .. forest 0.85
      sm[j * N1 + i] = grassBladeScale(wx, wz, h); // 2 forest/irradiated -> wider blades, 0.5 desert, 1 else
      tm[j * N1 + i] = grassBladeTall(wx, wz, h);  // EXTRA height: 2 forest (tall grass), 1 elsewhere
      grassTintAt(wx, wz, h, cm, (j * N1 + i) * 3); // blade tint: dry desert / toxic irradiated, else (0,0,0)
      groundTint(wx, wz, h, gm, (j * N1 + i) * 3);  // the TERRAIN's ground colour here -- roots anchor to it
    }
    const snowA = TERRAIN.snowStart, snowB = TERRAIN.snowEnd;
    // Grass starts ABOVE the beach sand. The terrain shader paints sand from the waterline up to about
    // seaLevel+8, so fade grass in over that upper sand band -> no blades sitting on the beach.
    const sandA = TERRAIN.seaLevel + 4, sandB = TERRAIN.seaLevel + 8;
    for (let b = 0; b < BLADES; b++) {
      // Always consume the same rng so placing a foundation never reshuffles untouched blades.
      const rx = rng(), rz = rng();
      const yaw = rng() * Math.PI * 2;
      const tip = rng() * Math.PI * 2;
      const rh = rng();
      const keepRoll = rng(); // density test draw: consumed every blade so placement stays deterministic
      const rt = rng();       // per-blade height-variation draw (forest grass varies 1..bladeTall, not uniform)
      const x = x0 + rx * CELL, z = z0 + rz * CELL;
      const o = b * VPER;
      const u = x * invTex, vv = z * invTex;
      for (let k = 0; k < VPER; k++) { uv[(o + k) * 2] = u; uv[(o + k) * 2 + 1] = vv; }
      // Cell-gradient slope + bilinear base height from the coarse grid (the cell the blade falls in).
      const fi = rx * HM_R, fj = rz * HM_R;
      let i = fi | 0, j = fj | 0; if (i >= HM_R) i = HM_R - 1; if (j >= HM_R) j = HM_R - 1;
      const h00 = hm[j * N1 + i], h10 = hm[j * N1 + i + 1], h01 = hm[(j + 1) * N1 + i], h11 = hm[(j + 1) * N1 + i + 1];
      // Base Y: bilinear over the ~1m coarse grid (HM_R=12). This is sub-cm off the analytic ground --
      // finer than the rendered terrain itself -- so blades seat cleanly without a terrainHeight() call
      // per blade (that per-blade fbm was the bulk of the recenter cost).
      const tx = fi - i, tz = fj - j;
      const y = (h00 * (1 - tx) + h10 * tx) * (1 - tz) + (h01 * (1 - tx) + h11 * tx) * tz;
      const dhx = ((h10 - h00) + (h11 - h01)) * 0.5 / step;
      const dhz = ((h01 - h00) + (h11 - h10)) * 0.5 / step;
      const ny = 1 / Math.sqrt(1 + dhx * dhx + dhz * dhz);
      // Grass-allowed biome mask, bilinear over the same coarse grid (blades fade out across a desert/rock
      // border instead of cutting hard).
      const b00 = bm[j * N1 + i], b10 = bm[j * N1 + i + 1], b01 = bm[(j + 1) * N1 + i], b11 = bm[(j + 1) * N1 + i + 1];
      const biomeGrass = (b00 * (1 - tx) + b10 * tx) * (1 - tz) + (b01 * (1 - tx) + b11 * tx) * tz; // 0..~0.85 density mult
      const s00 = sm[j * N1 + i], s10 = sm[j * N1 + i + 1], s01 = sm[(j + 1) * N1 + i], s11 = sm[(j + 1) * N1 + i + 1];
      const bladeScale = (s00 * (1 - tx) + s10 * tx) * (1 - tz) + (s01 * (1 - tx) + s11 * tx) * tz; // 0.5..2 (desert..forest)
      const t00 = tm[j * N1 + i], t10 = tm[j * N1 + i + 1], t01 = tm[(j + 1) * N1 + i], t11 = tm[(j + 1) * N1 + i + 1];
      const bladeTallMax = (t00 * (1 - tx) + t10 * tx) * (1 - tz) + (t01 * (1 - tx) + t11 * tx) * tz; // biome max
      const bladeTall = 1 + (bladeTallMax - 1) * rt; // per-blade: 1..max (forest 1..1.6, desert 1..3) so heights vary
      // Bilinear blade tint + TERRAIN ground colour (linear RGB) over the same coarse cell, written to
      // all 5 verts (a blade is one biome; the root colour anchors the blade to the ground it grew from).
      const ci = (j * N1 + i) * 3, ci1 = (j * N1 + i + 1) * 3, cj = ((j + 1) * N1 + i) * 3, cj1 = ((j + 1) * N1 + i + 1) * 3;
      for (let cc = 0; cc < 3; cc++) {
        const tv = (cm[ci + cc] * (1 - tx) + cm[ci1 + cc] * tx) * (1 - tz) + (cm[cj + cc] * (1 - tx) + cm[cj1 + cc] * tx) * tz;
        const gv = (gm[ci + cc] * (1 - tx) + gm[ci1 + cc] * tx) * (1 - tz) + (gm[cj + cc] * (1 - tx) + gm[cj1 + cc] * tx) * tz;
        for (let k = 0; k < VPER; k++) { tint[(o + k) * 3 + cc] = tv; root[(o + k) * 3 + cc] = gv; }
      }
      // Terrain grass gate (slope / snow line / beach sand) WITHOUT the biome factor: the per-biome density
      // scales keepP below, so a thinner biome keeps proportionally fewer blades at full quality.
      const grass = smoothstep01(0.80, 0.92, ny) * (1 - smoothstep01(snowA, snowB, y)) * smoothstep01(sandA, sandB, y);
      for (let k = 0; k < VPER; k++) gy[o + k] = y;
      // CONTINUOUS DENSITY: two cheap value-noise scales (reusing vnoiseT) give a 0..1 cover field --
      // broad meadows/bare patches modulated by finer clumps. Multiply by the biome factor so cover goes
      // sparse on the dirt/beach/slope fringe -> full grassland -> thick where green AND noise are high.
      const dBroad = vnoiseT(x * 0.012, z * 0.012, 256, 8101);
      const dFine = vnoiseT(x * 0.07, z * 0.07, 256, 5237);
      const cover = clamp01(dBroad * 0.7 + dFine * 0.5);
      // WORN PATCHES: a mid-scale noise carves lighter, thinner spots into otherwise-uniform fields
      // (forest floors get bare-ish runs, meadows get mowed-looking sweeps) -- variety, not carpet.
      const worn = smoothstep01(0.60, 0.78, vnoiseT(x * 0.033, z * 0.033, 256, 9337));
      // MEADOW PATCHES (owner 2026-07-13): a broad field marks knee-high meadows -- denser AND taller,
      // easing in from the edge. meadow=0 leaves the base grass exactly as it was.
      const meadow = smoothstep01(GRASS.meadowThresh, GRASS.meadowFull, vnoiseT(x * GRASS.meadowScale, z * GRASS.meadowScale, 256, 4159));
      // Keep PROBABILITY: across many blades the surviving fraction ~= keepP, so blade DENSITY tracks this
      // smoothly. The 0.35 floor keeps a few pioneers on bare dirt; it is gated off on sand/snow because
      // smoothstep01(0.06,0.55,grass) is 0 wherever the sand/snow gate zeroed `grass`.
      // Density tracks the terrain gate AND the per-biome multiplier: sparse biomes (mountain 0.2, hills 0.4)
      // keep proportionally fewer blades, green biomes ~0.85. A meadow pushes the kept fraction toward 1
      // (and softens the worn carve-outs, so its core reads unbroken).
      let keepP = smoothstep01(0.06, 0.55, grass) * (0.35 + 0.65 * cover) * biomeGrass * (1 - 0.45 * worn * (1 - meadow));
      keepP += (1 - keepP) * GRASS.meadowKeep * meadow * smoothstep01(0.06, 0.55, grass); // still gated off sand/snow/rock
      if (keepRoll > keepP) {
        for (let k = 0; k < VPER; k++) { const p = (o + k) * 3; pos[p] = x; pos[p + 1] = y; pos[p + 2] = z; }
        continue;
      }
      // FOUNDATION FLATTENING: under + near placed foundations the grass survives but ULTRA TINY --
      // pressed flat by the build -- easing back to full height over ~1.2m of clearance.
      const ff = this.footprintFactor(x, z);
      // Blade size: greener+denser = taller, then scaled by the biome blade size (forest = 2x for a dense floor),
      // then the meadow boost (up to 1+meadowTall at a patch core; rh keeps per-blade variety inside it).
      const h = (GRASS.bladeHeight + rh * GRASS.bladeHeightVar) * (0.45 + 0.4 * grass + 0.15 * cover) * bladeScale * bladeTall
        * (1 - 0.4 * worn * (1 - meadow)) * (1 + GRASS.meadowTall * meadow * (0.7 + 0.3 * rh)) * (0.08 + 0.92 * ff);
      const Wb = W * bladeScale, MWb = MW * bladeScale; // per-blade width scaled with the biome (height also * bladeTall)
      const yx = Math.sin(yaw), yz = -Math.cos(yaw);
      const tcx = Math.sin(tip), tcz = -Math.cos(tip);
      const tipOff = h * 0.12;
      // [bl, br, tr, tl, tc]
      setV(pos, o + 0, x + yx * (Wb / 2), y, z + yz * (Wb / 2));
      setV(pos, o + 1, x - yx * (Wb / 2), y, z - yz * (Wb / 2));
      setV(pos, o + 2, x - yx * (MWb / 2), y + h * 0.5, z - yz * (MWb / 2));
      setV(pos, o + 3, x + yx * (MWb / 2), y + h * 0.5, z + yz * (MWb / 2));
      setV(pos, o + 4, x + tcx * tipOff, y + h, z + tcz * tipOff);
    }
    ch.geom.attributes.position.needsUpdate = true;
    ch.geom.attributes.uv.needsUpdate = true;
    ch.geom.attributes.groundY.needsUpdate = true;
    ch.geom.attributes.tint.needsUpdate = true;
    ch.geom.attributes.rootCol.needsUpdate = true;
    ch.geom.computeBoundingSphere(); // keep the bounds in sync so frustum culling is correct
  }

  // 0 inside a foundation footprint .. 1 fully clear (eases over 1.2m past the edge). Drives the
  // pressed-flat mini-grass under and around builds.
  private footprintFactor(x: number, z: number): number {
    let f = 1;
    for (const fo of this.foundations) {
      if (insideFoundation(x, z, fo)) return 0;
      const dx = x - fo.x, dz = z - fo.z;
      const c = Math.cos(fo.yaw), s = Math.sin(fo.yaw);
      const lx = c * dx - s * dz, lz = s * dx + c * dz;
      let dist: number;
      if (fo.type === 'square') {
        const qx = Math.max(Math.abs(lx) - FOUND_S / 2, 0), qz = Math.max(Math.abs(lz) - FOUND_S / 2, 0);
        dist = Math.hypot(qx, qz);
      } else {
        dist = Math.max(0, Math.hypot(lx, lz) - FOUND_TRI_R); // triangle: circumradius approximation
      }
      f = Math.min(f, Math.min(1, dist / 1.2));
    }
    // Lightning scorch: grass is cleared inside the burn radius, easing back over ~1.2m past the rim.
    for (const b of this.burns) {
      const d = Math.hypot(x - b.x, z - b.z);
      if (d < b.r) return 0;
      f = Math.min(f, Math.min(1, (d - b.r) / 1.2));
    }
    return f;
  }

  // Register a permanent lightning scorch: clears grass in a circle. Re-bakes any live chunk it touches; far
  // chunks pick it up when they next stream in (footprintFactor reads this list on every bake).
  addBurn(x: number, z: number, r: number): void {
    this.burns.push({ x, z, r });
    if (this.burns.length > 120) this.burns.shift(); // bound it (oldest scorch fades from memory)
    const reach = CELL * 0.5 + r + 2;
    for (const ch of this.chunks) {
      if (!Number.isFinite(ch.cellX)) continue;
      const ccx = (ch.cellX + 0.5) * CELL, ccz = (ch.cellZ + 0.5) * CELL;
      if (Math.abs(ccx - x) <= reach && Math.abs(ccz - z) <= reach) this.enqueue(ch, false);
    }
  }

  // Foundation footprints to cut grass out of. Only the live chunks near a foundation that was added
  // OR removed are queued for a re-cut (so demolishing regrows grass too), and they stay visible while
  // they wait -- so editing a base never re-bakes the whole field or blinks the grass.
  setFoundations(list: ReadonlyArray<Foundation>): void {
    const next = list.map((f) => ({ type: f.type, x: f.x, z: f.z, yaw: f.yaw }));
    const reach = CELL * 0.5 + FOUND_S; // a foundation can cut blades up to ~its size past a cell edge
    const touches = (ccx: number, ccz: number, fs: ReadonlyArray<Foundation>): boolean => {
      for (const f of fs) if (Math.abs(f.x - ccx) <= reach && Math.abs(f.z - ccz) <= reach) return true;
      return false;
    };
    for (const ch of this.chunks) {
      if (!Number.isFinite(ch.cellX)) continue;
      const ccx = (ch.cellX + 0.5) * CELL, ccz = (ch.cellZ + 0.5) * CELL;
      if (touches(ccx, ccz, this.foundations) || touches(ccx, ccz, next)) this.enqueue(ch, false);
    }
    this.foundations = next;
  }
}

function setV(pos: Float32Array, vi: number, x: number, y: number, z: number): void {
  const p = vi * 3;
  pos[p] = x; pos[p + 1] = y; pos[p + 2] = z;
}

// Point-in-footprint test in the foundation's local frame (square box or equilateral triangle).
function insideFoundation(px: number, pz: number, f: Foundation): boolean {
  const dx = px - f.x;
  const dz = pz - f.z;
  const c = Math.cos(f.yaw);
  const s = Math.sin(f.yaw);
  const lx = c * dx - s * dz;
  const lz = s * dx + c * dz;
  if (f.type === 'square') return Math.abs(lx) <= FOUND_S / 2 && Math.abs(lz) <= FOUND_S / 2;
  const R = FOUND_TRI_R;
  const v0x = 0, v0z = R, v1x = 0.8660254 * R, v1z = -0.5 * R, v2x = -0.8660254 * R, v2z = -0.5 * R;
  const sign = (ax: number, az: number, bx: number, bz: number, cx: number, cz: number): number =>
    (ax - cx) * (bz - cz) - (bx - cx) * (az - cz);
  const d1 = sign(lx, lz, v0x, v0z, v1x, v1z);
  const d2 = sign(lx, lz, v1x, v1z, v2x, v2z);
  const d3 = sign(lx, lz, v2x, v2z, v0x, v0z);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}
